import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import {type UIGameMechanic} from '../UIGameEventController.ts'
import {type BankTransactionLedger} from '@colonist/game-state-types'
import {CardDataController, type CardExchangeFormat} from '@colonist/game-card-types'
import {ArrayUtils} from '@colonist/utils'
import {UIGameBeginnerModeCardsHelper} from '../ui_game_beginner_mode/ui_game_beginner_mode_helpers/UIGameBeginnerModeCardsHelper.ts'
import {BeginnerModeConstants} from '@colonist/game-beginner-mode'
import {PIXI} from '@colonist/pixi'
import {type CardEnum, type MechanicRobberState, PlayerColor, TurnState} from '@colonist/game-states'
import {Point} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {getGlobalPositionOfPlayerCard, getPointPositionFromBank, getPointPositionFromPlayerInventory, getCardDimensionsFromPlayerInventory} from '@colonist/ui-game'
import {ModeSettingEnum} from '@colonist/types'

interface CardAnimationOptions {
    speed?: number
    delay?: number
    onComplete?: () => void
}

export class UIGameCardController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    private readonly robberState: MechanicRobberState | undefined

    private nextAnimationId = 0
    protected readonly cardsAnimatingToMyHand: {cardEnum: CardEnum, animationId: number}[] = []
    protected readonly cardsToBeAddedFromTile: BankTransactionLedger[] = []

    beginnerModeHelper?: UIGameBeginnerModeCardsHelper

    private static readonly CARD_ANIMATION_DELAY = 100
    private static readonly CARD_ANIMATION_DELAY_PER_CARD = 15

    constructor(uiGameManager: UIGameManager, robberState: MechanicRobberState | undefined) {
        this.uiGameManager = uiGameManager
        this.robberState = robberState
        this.uiGameManager.eventController.registerUIGameMechanic(this)
    }

    getCardInventory(): CardEnum[] {
        const cardsInInventory = this.uiGameManager.gameStore.getState().gameCardInventory.cardInventory
        const cardsAnimatingToMyHand = this.cardsAnimatingToMyHand.map((card) => card.cardEnum)
        return [...cardsInInventory, ...cardsAnimatingToMyHand]
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.GivePlayerCardFromTile: this.addCardsFromTile(data); break
            case UIGameEventEnums.ExchangeCards: this.exchangeCards(data); break
            case UIGameEventEnums.PlayerControllerStateUpdated: this.onPlayerControllerStateUpdated(); break
            case UIGameEventEnums.TileNumberAnimationEnd: this.distributePendingCards(); break
            default: break
        }
    }

    initBeginnerModeHelper() {
        this.beginnerModeHelper = new UIGameBeginnerModeCardsHelper(this.uiGameManager)
    }

    private onPlayerControllerStateUpdated() {
        const myCurrentCardEnums = this.getCardInventory().sort()
        this.beginnerModeHelper?.addPendingCardsToMyCurrentCardEnums(myCurrentCardEnums, this.cardsToBeAddedFromTile)
    }

    protected highlightTileDuringInitialPlacement(tileIndex: number) {
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.InitialPlacement) return
        const tileToHighlight = this.uiGameManager.mapController.tileViews[tileIndex]
        const animationDurationInSeconds = 0.5
        tileToHighlight.highlightTileForTime(animationDurationInSeconds)
    }

    protected distributePendingCards() {
        const tileIndexToAmountOfCardsProducedFromTile = this.getTileIndexToAmountOfCardsProduced()
        const tileIndexToCardIndexMap = new Map<number, number>()

        for(const transaction of this.cardsToBeAddedFromTile) {
            if(!tileIndexToCardIndexMap.has(transaction.tileIndex)) tileIndexToCardIndexMap.set(transaction.tileIndex, 0)
            const cardIndex = tileIndexToCardIndexMap.get(transaction.tileIndex) ?? 0
            const totalNumberOfCardsProducedFromTile = (tileIndexToAmountOfCardsProducedFromTile.get(transaction.tileIndex) ?? 0)
            this.addCardFromTile([transaction.card], transaction.tileIndex, transaction.owner, cardIndex, totalNumberOfCardsProducedFromTile)
            tileIndexToCardIndexMap.set(transaction.tileIndex, cardIndex + 1)
        }
        this.cardsToBeAddedFromTile.length = 0
    }

    private getTileIndexToAmountOfCardsProduced(): Map<number, number> {
        const tileIndexToAmountOfCardsProduced = new Map<number, number>()
        for(const transaction of this.cardsToBeAddedFromTile) {
            if(!tileIndexToAmountOfCardsProduced.has(transaction.tileIndex)) tileIndexToAmountOfCardsProduced.set(transaction.tileIndex, 0)
            const cardCount = tileIndexToAmountOfCardsProduced.get(transaction.tileIndex) ?? 0
            tileIndexToAmountOfCardsProduced.set(transaction.tileIndex, cardCount + 1)
        }
        return tileIndexToAmountOfCardsProduced
    }

    protected exchangeCards(exchangeData: CardExchangeFormat) {
        const makeCardsFlyToRobber = this.beginnerModeHelper?.shouldCardsFlyToRobber && exchangeData.givingPlayer == this.uiGameManager.gameController.myColor
        makeCardsFlyToRobber ? this.giveCardsToRobber(exchangeData) : this.playerExchange(exchangeData.receivingPlayer, exchangeData.givingPlayer, exchangeData.givingCards)
        this.playerExchange(exchangeData.givingPlayer, exchangeData.receivingPlayer, exchangeData.receivingCards)
    }

    protected addCardsFromTile(transactions: BankTransactionLedger[]) {
        this.cardsToBeAddedFromTile.push(...transactions)

        if(this.shouldDisplayBeginnerModeCardAnimation()) return
        this.distributePendingCards()
    }

    private shouldDisplayBeginnerModeCardAnimation(): boolean {
        const isBeginnerMode = this.beginnerModeHelper != undefined
        if(!isBeginnerMode) return false

        const isInitialResourceDistributionInBeginnerMode = this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement
        return !isInitialResourceDistributionInBeginnerMode
    }

    private playerExchange(receivingPlayer: PlayerColor, givingPlayer: PlayerColor, cards: CardEnum[]) {
        for(let i = 0; i < cards.length; i++) {
            const cardToGive = cards[i]
            const startPoint = this.getCardAnimationPointForPlayer(givingPlayer, cardToGive)
            const endPoint = this.getCardAnimationPointForPlayer(receivingPlayer, cardToGive)

            const delay = UIGameCardController.CARD_ANIMATION_DELAY + i * UIGameCardController.CARD_ANIMATION_DELAY_PER_CARD
            this.createCardAnimation(cardToGive, startPoint, endPoint, givingPlayer, receivingPlayer, {
                delay: delay,
            })
        }
    }

    private getCardAnimationPointForPlayer(player: PlayerColor, cardEnum: CardEnum): Point {
        const isBankTrade = player == PlayerColor.None
        if(isBankTrade) return getPointPositionFromBank(cardEnum) ?? new Point(0, 0)

        const isCardComingToMyHand = player == this.uiGameManager.gameController.myColor

        if(isCardComingToMyHand) {
            const pointFromDOMInventory = getPointPositionFromPlayerInventory(cardEnum)
            if(!pointFromDOMInventory) return new Point(0, 0)
            return pointFromDOMInventory
        }

        const isResourceCard = CardDataController.isResourceCard(cardEnum)
        const opponentPoint = this.getPointFromPlayer(player, isResourceCard)
        if(opponentPoint == undefined) return new Point(0, 0)
        return opponentPoint
    }

    // eslint-disable-next-line class-methods-use-this
    private getPointFromPlayer(player: PlayerColor, isResourceCard: boolean): Point {
        return getGlobalPositionOfPlayerCard(player, isResourceCard) ?? new Point(0, 0)
    }

    protected giveCardsToRobber(exchangeData: CardExchangeFormat) {
        if(this.robberState == undefined) return
        const robberTileIndex = this.robberState == undefined ? 11 : this.robberState.locationTileIndex
        const cards = exchangeData.givingCards
        for(const card of cards) {
            const endPoint = this.getPointFromMap(robberTileIndex)
            const startPoint = this.getCardAnimationPointForPlayer(this.uiGameManager.gameController.myColor, card)
            this.createCardAnimation(card, startPoint, endPoint, this.uiGameManager.gameController.myColor, PlayerColor.None)
        }
    }

    protected addCardFromTile(cards: CardEnum[], tileIndex: number, toPlayer: PlayerColor, cardIndex: number, totalNumberOfCardsProducedFromTile: number) {
        this.highlightTileDuringInitialPlacement(tileIndex)
        const animationStartPoint = this.getPointFromMap(tileIndex)

        for(const cardEnum of cards) {
            const animationEndPoint = this.getCardAnimationPointForPlayer(toPlayer, cardEnum)
            if(this.beginnerModeHelper == undefined) {
                const delay = UIGameCardController.CARD_ANIMATION_DELAY + cardIndex * UIGameCardController.CARD_ANIMATION_DELAY_PER_CARD
                this.createCardAnimation(cardEnum, animationStartPoint, animationEndPoint, PlayerColor.None, toPlayer, {
                    delay: delay,
                })
                return
            }

            const orderOfThisCard = cardIndex
            const amountOfCardsProducedFromTile = totalNumberOfCardsProducedFromTile ?? 1
            const cardDimensions = getCardDimensionsFromPlayerInventory()
            const cardW = cardDimensions.width
            const riseAnimationEndPointX = animationStartPoint.x + cardW * (orderOfThisCard - (amountOfCardsProducedFromTile - 1) / 2)
            const riseAnimationEndPoint = new Point(riseAnimationEndPointX, animationStartPoint.y)
            this.addCardFromTileWithRiseAnimation(cardEnum, riseAnimationEndPoint, animationEndPoint, toPlayer)
        }
    }

    private addCardFromTileWithRiseAnimation(cardEnum: CardEnum, cardAnimationStartPoint: Point, cardAnimationEndPoint: Point, toPlayer: PlayerColor) {
        const riseAnimationSpeed = 0.5
        const cardDimensions = getCardDimensionsFromPlayerInventory()
        const cardH = cardDimensions.height
        const riseAnimationEndPoint = new Point(cardAnimationStartPoint.x, cardAnimationStartPoint.y - cardH)
        const cardAnimationWaitMS = this.beginnerModeHelper?.cardFlyAnimationWaitMS ?? 1000

        const afterRiseAnimation = () => {
            this.createCardAnimation(cardEnum, riseAnimationEndPoint, cardAnimationEndPoint, PlayerColor.None, toPlayer, {
                delay: cardAnimationWaitMS,
            })
        }

        const animationSpeed = (BeginnerModeConstants.BASE_WAIT_MS_FOR_CARD_FLY_ANIMATION / cardAnimationWaitMS) * riseAnimationSpeed
        this.createCardAnimation(cardEnum, cardAnimationStartPoint, riseAnimationEndPoint, PlayerColor.None, PlayerColor.None, {
            speed: animationSpeed,
            onComplete: afterRiseAnimation,
        })
    }

    private getPointFromMap(tileIndex: number): Point {
        const half = 2
        const tile = this.uiGameManager.mapController.tileViews[tileIndex]
        const margin = this.uiGameManager.canvasController.margin

        const cardDimensions = getCardDimensionsFromPlayerInventory()
        const cardW = cardDimensions.width
        const cardH = cardDimensions.height

        const x = tile.x + tile.width / half - cardW / half + margin
        const y = tile.y + tile.height / half - cardH / half
        const worldPoint = new PIXI.Point(x, y)
        return this.uiGameManager.mapController.worldToScreenPoint(worldPoint)
    }

    private removeMyCardForAnimation(cardEnum: CardEnum) {
        const isRemovedFromInventory = this.removeMyCardForAnimationFromInventory(cardEnum)
        if(isRemovedFromInventory) return

        const isRemovedFromFlyingCards = this.removeMyCardForAnimationFromFlyingCards(cardEnum)
        if(isRemovedFromFlyingCards) return

        // eslint-disable-next-line no-console
        console.error('Card not found in inventory or flying cards for animation', cardEnum)
    }

    private removeMyCardForAnimationFromInventory(cardEnum: CardEnum): boolean {
        const cardInventory = this.uiGameManager.gameStore.getState().gameCardInventory.cardInventory
        if(cardInventory.includes(cardEnum)) {
            return true
        }
        return false
    }

    private removeMyCardForAnimationFromFlyingCards(cardEnum: CardEnum): boolean {
        const existingFlyingCard = this.cardsAnimatingToMyHand.find((card) => card.cardEnum === cardEnum)
        if(existingFlyingCard) {
            this.uiGameManager.gameStore.getState().gameCardAnimator.forceAnimationComplete(existingFlyingCard.animationId)
            return this.removeMyCardForAnimationFromInventory(cardEnum)
        }

        return false
    }

    private createCardAnimation(cardEnum: CardEnum, startPoint: Point, endPoint: Point, startPlayer: PlayerColor, endPlayer: PlayerColor, options?: CardAnimationOptions) {
        const animationId = this.nextAnimationId
        this.nextAnimationId += 1

        const isAnimatingToMyHand = endPlayer == this.uiGameManager.gameController.myColor && !this.uiGameManager.gameController.isPlayerSpectator
        if(isAnimatingToMyHand) this.cardsAnimatingToMyHand.push({cardEnum: cardEnum, animationId: animationId})

        const isAnimatingFromMyHand = startPlayer == this.uiGameManager.gameController.myColor && !this.uiGameManager.gameController.isPlayerSpectator
        if(isAnimatingFromMyHand) this.removeMyCardForAnimation(cardEnum)

        const onComplete = (id: number) => {
            this.uiGameManager.gameStore.getState().gameCardAnimator.removeCard(id)
            options?.onComplete?.()
            this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.CardAnimationEnd)

            if(isAnimatingToMyHand) {
                const existingFlyingCard = this.cardsAnimatingToMyHand.find((card) => card.animationId === animationId)
                if(existingFlyingCard) ArrayUtils.removeElementFromArray(this.cardsAnimatingToMyHand, existingFlyingCard)
            }
        }

        const appSize = Math.max(this.uiGameManager.canvasController.appW, this.uiGameManager.canvasController.appH)
        const speedForResolution = appSize / 2000
        const speedForGameMode = UIGameCardController.getSpeedForGameMode(this.uiGameManager.gameController.gameSettings.modeSetting)
        const speed = (options?.speed ?? 1) * speedForResolution * speedForGameMode

        this.uiGameManager.gameStore.getState().gameCardAnimator.addCard({
            id: animationId,
            card: {
                cardEnum: cardEnum,
            },
            isAnimatingToMyHand: isAnimatingToMyHand,
            from: startPoint,
            to: endPoint,
            speed: speed,
            delay: options?.delay ?? UIGameCardController.CARD_ANIMATION_DELAY,
            onComplete: onComplete,
        })
    }

    private static getSpeedForGameMode(mode: ModeSettingEnum): number {
        switch(mode) {
            case ModeSettingEnum.ColonistRush4P: return 1.25
            default: return 1
        }
    }
}
